const BOUNDS_EPSILON = 1e-9;
const DEFAULT_MAX_ITEMS_PER_NODE = 32;
const DEFAULT_MAX_DEPTH = 12;

const isFinitePair = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  Number.isFinite(Number(value[0])) &&
  Number.isFinite(Number(value[1]));

const pointInBounds = (bounds, lon, lat) => {
  if (!Array.isArray(bounds) || bounds.length !== 4) return false;
  return (
    lon >= bounds[0] - BOUNDS_EPSILON &&
    lon <= bounds[2] + BOUNDS_EPSILON &&
    lat >= bounds[1] - BOUNDS_EPSILON &&
    lat <= bounds[3] + BOUNDS_EPSILON
  );
};

const boundsIntersect = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== 4 || b.length !== 4) return false;
  return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
};

const childIndexForBounds = (parentBounds, childBounds) => {
  const midX = (parentBounds[0] + parentBounds[2]) / 2;
  const midY = (parentBounds[1] + parentBounds[3]) / 2;

  const inWest = childBounds[2] <= midX;
  const inEast = childBounds[0] >= midX;
  const inSouth = childBounds[3] <= midY;
  const inNorth = childBounds[1] >= midY;

  if (inWest && inSouth) return 0;
  if (inEast && inSouth) return 1;
  if (inWest && inNorth) return 2;
  if (inEast && inNorth) return 3;

  return -1;
};

const createNode = (bounds, depth) => ({
  bounds,
  depth,
  items: [],
  children: null
});

const splitNode = (node) => {
  const [minX, minY, maxX, maxY] = node.bounds;
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;

  node.children = [
    createNode([minX, minY, midX, midY], node.depth + 1),
    createNode([midX, minY, maxX, midY], node.depth + 1),
    createNode([minX, midY, midX, maxY], node.depth + 1),
    createNode([midX, midY, maxX, maxY], node.depth + 1)
  ];
};

const insertItem = (node, item, maxItemsPerNode, maxDepth) => {
  if (!boundsIntersect(node.bounds, item.bounds)) return;

  if (node.children) {
    const childIndex = childIndexForBounds(node.bounds, item.bounds);
    if (childIndex !== -1) {
      insertItem(node.children[childIndex], item, maxItemsPerNode, maxDepth);
      return;
    }
  }

  node.items.push(item);

  if (node.items.length <= maxItemsPerNode || node.depth >= maxDepth) {
    return;
  }

  if (!node.children) {
    splitNode(node);
  }

  const retained = [];
  node.items.forEach((existing) => {
    const childIndex = childIndexForBounds(node.bounds, existing.bounds);
    if (childIndex === -1) {
      retained.push(existing);
      return;
    }
    insertItem(node.children[childIndex], existing, maxItemsPerNode, maxDepth);
  });
  node.items = retained;
};

const collectPointMatches = (node, lon, lat, results) => {
  if (!pointInBounds(node.bounds, lon, lat)) return;

  node.items.forEach((item) => {
    if (pointInBounds(item.bounds, lon, lat)) {
      results.add(item.id);
    }
  });

  if (!node.children) return;
  node.children.forEach((child) => collectPointMatches(child, lon, lat, results));
};

const normalizeAround = (value, reference) => {
  let adjusted = value;
  while (adjusted - reference > 180) adjusted -= 360;
  while (adjusted - reference < -180) adjusted += 360;
  return adjusted;
};

const ringContainsPoint = (ring, lon, lat) => {
  if (!Array.isArray(ring) || ring.length < 3) return false;

  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = Number(ring[i]?.[0]);
    const yi = Number(ring[i]?.[1]);
    const xj = Number(ring[j]?.[0]);
    const yj = Number(ring[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;

    const xiAdjusted = normalizeAround(xi, lon);
    const xjAdjusted = normalizeAround(xj, lon);

    const intersects =
      (yi > lat) !== (yj > lat) &&
      lon < ((xjAdjusted - xiAdjusted) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xiAdjusted;
    if (intersects) inside = !inside;
  }

  return inside;
};

const computeRingBounds = (ring) => {
  if (!Array.isArray(ring)) return null;
  const bounds = [Infinity, Infinity, -Infinity, -Infinity];

  ring.forEach((coordinate) => {
    if (!isFinitePair(coordinate)) return;
    const lon = Number(coordinate[0]);
    const lat = Number(coordinate[1]);
    bounds[0] = Math.min(bounds[0], lon);
    bounds[1] = Math.min(bounds[1], lat);
    bounds[2] = Math.max(bounds[2], lon);
    bounds[3] = Math.max(bounds[3], lat);
  });

  return bounds.every(Number.isFinite) ? bounds : null;
};

const polygonContainsPoint = (polygon, lon, lat) => {
  if (!polygon || !pointInBounds(polygon.bounds, lon, lat)) return false;

  if (!ringContainsPoint(polygon.outerRing, lon, lat)) return false;

  for (let i = 0; i < polygon.holes.length; i += 1) {
    const hole = polygon.holes[i];
    if (!pointInBounds(hole.bounds, lon, lat)) continue;
    if (ringContainsPoint(hole.ring, lon, lat)) return false;
  }

  return true;
};

const emptyResult = {
  bounds: null,
  polygons: [],
  tree: null
};

export const buildGeoJSONSpatialIndex = (
  geoJSON,
  options = {}
) => {
  if (!geoJSON || typeof geoJSON !== 'object') return emptyResult;

  const maxItemsPerNode = Number.isFinite(options.maxItemsPerNode)
    ? Math.max(4, Math.floor(options.maxItemsPerNode))
    : DEFAULT_MAX_ITEMS_PER_NODE;
  const maxDepth = Number.isFinite(options.maxDepth)
    ? Math.max(2, Math.floor(options.maxDepth))
    : DEFAULT_MAX_DEPTH;

  const polygons = [];
  const totalBounds = [Infinity, Infinity, -Infinity, -Infinity];

  const pushBounds = (bounds) => {
    totalBounds[0] = Math.min(totalBounds[0], bounds[0]);
    totalBounds[1] = Math.min(totalBounds[1], bounds[1]);
    totalBounds[2] = Math.max(totalBounds[2], bounds[2]);
    totalBounds[3] = Math.max(totalBounds[3], bounds[3]);
  };

  const pushPolygon = (polygonCoordinates) => {
    if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length === 0) return;
    const outerRing = polygonCoordinates[0];
    const outerBounds = computeRingBounds(outerRing);
    if (!outerBounds) return;

    const holes = polygonCoordinates
      .slice(1)
      .map((ring) => {
        const bounds = computeRingBounds(ring);
        if (!bounds) return null;
        return { ring, bounds };
      })
      .filter(Boolean);

    polygons.push({
      outerRing,
      holes,
      bounds: outerBounds
    });
    pushBounds(outerBounds);
  };

  const pushGeometry = (geometry) => {
    if (!geometry || typeof geometry !== 'object') return;

    if (geometry.type === 'Polygon') {
      pushPolygon(geometry.coordinates);
      return;
    }

    if (geometry.type === 'MultiPolygon') {
      const polygonsList = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
      polygonsList.forEach((polygonCoordinates) => pushPolygon(polygonCoordinates));
      return;
    }

    if (geometry.type === 'GeometryCollection') {
      const geometries = Array.isArray(geometry.geometries) ? geometry.geometries : [];
      geometries.forEach((entry) => pushGeometry(entry));
    }
  };

  const pushGeoJSON = (input) => {
    if (!input || typeof input !== 'object') return;

    if (input.type === 'FeatureCollection') {
      const features = Array.isArray(input.features) ? input.features : [];
      features.forEach((feature) => pushGeometry(feature?.geometry));
      return;
    }

    if (input.type === 'Feature') {
      pushGeometry(input.geometry);
      return;
    }

    pushGeometry(input);
  };

  pushGeoJSON(geoJSON);
  if (!polygons.length || !totalBounds.every(Number.isFinite)) {
    return emptyResult;
  }

  const rootBounds = [...totalBounds];
  if (Math.abs(rootBounds[2] - rootBounds[0]) < BOUNDS_EPSILON) {
    rootBounds[0] -= BOUNDS_EPSILON;
    rootBounds[2] += BOUNDS_EPSILON;
  }
  if (Math.abs(rootBounds[3] - rootBounds[1]) < BOUNDS_EPSILON) {
    rootBounds[1] -= BOUNDS_EPSILON;
    rootBounds[3] += BOUNDS_EPSILON;
  }

  const tree = createNode(rootBounds, 0);
  polygons.forEach((polygon, index) => {
    insertItem(tree, { id: index, bounds: polygon.bounds }, maxItemsPerNode, maxDepth);
  });

  return {
    bounds: rootBounds,
    polygons,
    tree
  };
};

export const pointInGeoJSONSpatialIndex = (spatialIndex, lon, lat) => {
  if (
    !spatialIndex ||
    !Array.isArray(spatialIndex.polygons) ||
    !spatialIndex.tree ||
    !Number.isFinite(lon) ||
    !Number.isFinite(lat)
  ) {
    return false;
  }

  if (!pointInBounds(spatialIndex.bounds, lon, lat)) return false;

  const candidatePolygonIds = new Set();
  collectPointMatches(spatialIndex.tree, lon, lat, candidatePolygonIds);
  if (!candidatePolygonIds.size) return false;

  for (const polygonId of candidatePolygonIds) {
    const polygon = spatialIndex.polygons[polygonId];
    if (!polygon) continue;
    if (polygonContainsPoint(polygon, lon, lat)) return true;
  }

  return false;
};
